import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import {
  ArrowLeft,
  Star,
  Clock,
  Calendar,
  Banknote,
  TrendingUp,
  Gift,
  Lock,
  Edit,
  Percent,
  ShieldCheck,
  Wallet,
  ArrowUpRight,
  Building2,
  Dumbbell,
  UserX,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Plus,
  Search,
} from "lucide-react";

import { API } from "@/App";
import TrainerScheduleModal from "@/components/TrainerScheduleModal";
import TrainerTimeBlockModal from "@/components/TrainerTimeBlockModal";
import TrainerCommissionReportModal from "@/components/TrainerCommissionReportModal";
import TrainerEditModal from "@/components/TrainerEditModal";
import TrainerVisibilityCard from "@/components/TrainerVisibilityCard";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import i18n from "@/i18n";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { DateInputTR } from "@/components/ui/date-picker-tr";

const formatTurkishCurrency = (amount) => {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
  }).format(amount || 0);
};

const getWorkTypeLabel = (workType, t) => {
  const labels = {
    commission_receiving: t("reports:commissionReceiving"),
    salary_receiving: t("employees:salaryReceiving"),
    salary_and_commission_receiving: t("reports:salaryAndCommission"),
    commission_giving: t("reports:commissionGiving"),
    rent_giving: t("common:rentGiving"),
  };
  return labels[workType] || workType || t("common:notSpecified");
};

const getWorkTypeIcon = (workType) => {
  switch (workType) {
    case "commission_receiving": return <TrendingUp className="w-4 h-4 text-green-600" />;
    case "salary_receiving": return <Wallet className="w-4 h-4 text-blue-600" />;
    case "salary_and_commission_receiving": return <Wallet className="w-4 h-4 text-purple-600" />;
    case "commission_giving": return <ArrowUpRight className="w-4 h-4 text-orange-600" />;
    case "rent_giving": return <Building2 className="w-4 h-4 text-amber-600" />;
    default: return <TrendingUp className="w-4 h-4 text-green-600" />;
  }
};

const getReservationStatusBadge = (status, t) => {
  const statusConfig = {
    pending: { label: t('common:pending'), color: "bg-yellow-100 text-yellow-700" },
    pending_member_approval: { label: t('common:pending'), color: "bg-yellow-100 text-yellow-700" },
    pending_business_approval: { label: t('common:pending'), color: "bg-amber-100 text-amber-700" },
    change_pending_member: { label: t('common:pending'), color: "bg-yellow-100 text-yellow-700" },
    change_pending_business: { label: t('common:pending'), color: "bg-amber-100 text-amber-700" },
    approved: { label: t('admin:approved'), color: "bg-green-100 text-green-700" },
    confirmed: { label: t('admin:approved'), color: "bg-green-100 text-green-700" },
    completed: { label: t('common:completed'), color: "bg-blue-100 text-blue-700" },
    cancelled: { label: t('common:cancel'), color: "bg-red-100 text-red-700" },
    cancelled_late: { label: t('reservations:lateCancellation'), color: "bg-red-100 text-red-700" },
    burned: { label: t("common:burned"), color: "bg-orange-100 text-orange-700" },
  };
  const config = statusConfig[status] || { label: status || t('common:unknown'), color: "bg-gray-100 text-gray-700" };
  return <Badge className={config.color}>{config.label}</Badge>;
};

export default function TrainerDetailPage({ businessId }) {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "reports", "packages", "employees", "admin"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [trainerCommission, setTrainerCommission] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [trainerDebts, setTrainerDebts] = useState(null); // New: combined salary/bonus data
  const [bonusPayments, setBonusPayments] = useState([]);
  
  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTimeBlockModal, setShowTimeBlockModal] = useState(false);
  const [showCommissionReportModal, setShowCommissionReportModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [endReason, setEndReason] = useState("");
  const [endOtherReason, setEndOtherReason] = useState(""); // "Diğer" seçildiğinde açıklama
  const [bonusForm, setBonusForm] = useState({
    amount: "",
    payment_date: todayLocalString(),
    reason: "",
    payment_method: "cash"
  });
  
  // Business data
  const [businessAcceptedCards, setBusinessAcceptedCards] = useState([]);
  const [businessTrainingTypes, setBusinessTrainingTypes] = useState([]);
  const [businessBranches, setBusinessBranches] = useState([]);
  const [businessWorkingHours, setBusinessWorkingHours] = useState({});
  
  // Current user for visibility check
  const [currentUser, setCurrentUser] = useState(null);
  
  // Check if this is the trainer's own profile
  const isOwnProfile = currentUser && trainer && trainer.user_id === currentUser.user_id;
  
  // Dersler filtre state'leri
  const [lessonSearchTerm, setLessonSearchTerm] = useState("");
  const [lessonBranchFilter, setLessonBranchFilter] = useState("all");
  const [lessonStatusFilter, setLessonStatusFilter] = useState("all");
  // Pagination
  const [lessonPage, setLessonPage] = useState(1);
  const [lessonPageSize, setLessonPageSize] = useState(10);
  
  // Filtrelenmiş ve sıralanmış dersler
  const filteredLessons = useMemo(() => {
    let result = [...lessons];
    
    // Tarihe göre sırala (en yeniden en eskiye)
    result.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Üye adına göre filtrele
    if (lessonSearchTerm) {
      const searchLower = lessonSearchTerm.toLowerCase();
      result = result.filter(lesson => 
        lesson.member_name?.toLowerCase().includes(searchLower)
      );
    }
    
    // Branşa göre filtrele
    if (lessonBranchFilter !== "all") {
      result = result.filter(lesson => lesson.branch === lessonBranchFilter);
    }
    
    // Duruma göre filtrele
    if (lessonStatusFilter !== "all") {
      if (lessonStatusFilter === "pending") {
        // Tüm beklemede durumlarını kapsasın
        const pendingStatuses = ["pending", "pending_member_approval", "pending_business_approval", "change_pending_member", "change_pending_business"];
        result = result.filter(lesson => pendingStatuses.includes(lesson.status));
      } else if (lessonStatusFilter === "confirmed") {
        // Onaylandı = confirmed + approved
        result = result.filter(lesson => lesson.status === "confirmed" || lesson.status === "approved");
      } else if (lessonStatusFilter === "cancelled") {
        // İptal = cancelled + cancelled_late
        result = result.filter(lesson => lesson.status === "cancelled" || lesson.status === "cancelled_late");
      } else {
        result = result.filter(lesson => lesson.status === lessonStatusFilter);
      }
    }
    
    return result;
  }, [lessons, lessonSearchTerm, lessonBranchFilter, lessonStatusFilter]);
  
  // Reset page when filters change
  useEffect(() => {
    setLessonPage(1);
  }, [lessonSearchTerm, lessonBranchFilter, lessonStatusFilter, lessonPageSize]);
  
  // Paginated lessons (slice for current page)
  const paginatedLessons = useMemo(() => {
    const start = (lessonPage - 1) * lessonPageSize;
    return filteredLessons.slice(start, start + lessonPageSize);
  }, [filteredLessons, lessonPage, lessonPageSize]);
  const totalLessonPages = Math.max(1, Math.ceil(filteredLessons.length / lessonPageSize));
  
  // Derslerdeki benzersiz branşları al
  const lessonBranches = useMemo(() => {
    const branches = new Set(lessons.map(l => l.branch).filter(Boolean));
    return Array.from(branches).sort();
  }, [lessons]);
  
  // Bu ay performans özeti
  const monthlyPerformance = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Bu ayki dersler
    const thisMonthLessons = lessons.filter(lesson => {
      const lessonDate = new Date(lesson.date);
      return lessonDate.getMonth() === currentMonth && lessonDate.getFullYear() === currentYear;
    });
    
    const totalLessons = thisMonthLessons.length;
    const completedLessons = thisMonthLessons.filter(l => l.status === "completed").length;
    const approvedLessons = thisMonthLessons.filter(l => l.status === "approved" || l.status === "confirmed").length;
    const cancelledLessons = thisMonthLessons.filter(l => l.status === "cancelled" || l.status === "cancelled_late").length;
    const pendingStatuses = ["pending", "pending_member_approval", "pending_business_approval", "change_pending_member", "change_pending_business"];
    const pendingLessons = thisMonthLessons.filter(l => pendingStatuses.includes(l.status)).length;
    
    // frozen_commission = backend'deki prim alanı, commission_amount fallback olarak eklendi
    const totalCommission = thisMonthLessons.reduce((sum, l) => sum + (l.frozen_commission || l.commission_amount || 0), 0);
    
    // Tamamlanma oranı (iptal olmayan derslerin tamamlanma oranı)
    const nonCancelledLessons = thisMonthLessons.filter(l => l.status !== "cancelled" && l.status !== "cancelled_late");
    const completionRate = nonCancelledLessons.length > 0 
      ? Math.round((completedLessons / nonCancelledLessons.length) * 100) 
      : 0;
    
    return {
      totalLessons,
      completedLessons,
      approvedLessons,
      cancelledLessons,
      pendingLessons,
      totalCommission,
      completionRate
    };
  }, [lessons]);

  useEffect(() => {
    if (trainerId && businessId) {
      fetchTrainerData();
      fetchBusinessData();
      fetchCurrentUser();
    }
  }, [trainerId, businessId]);

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };
  
  const fetchTrainerData = async () => {
    setLoading(true);
    try {
      // Fetch trainer details
      const trainerRes = await fetch(`${API}/trainers/${trainerId}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainerData = await trainerRes.json();
        setTrainer(trainerData);
        
        // Fetch related data in parallel
        await Promise.all([
          fetchTrainerCommission(),
          fetchLessons(),
          fetchSalaryPayments(),
          fetchBonusPayments(),
        ]);
      } else {
        toast.error(t("common:toast_antrenorBulunamadi"));
        navigate("/dashboard/trainers");
      }
    } catch (error) {
      console.error("Error fetching trainer:", error);
      toast.error(t('common:dataLoadError'));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTrainerCommission = async () => {
    try {
      const res = await fetch(`${API}/trainers/${trainerId}/my-commissions?period=current`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setTrainerCommission(data);
      }
    } catch (error) {
      console.error("Error fetching commission:", error);
    }
  };
  
  const fetchLessons = async () => {
    try {
      // Tüm rezervasyonları trainer_id ile filtrele (iptal dahil)
      const res = await fetch(`${API}/businesses/${businessId}/reservations?trainer_id=${trainerId}&limit=50&include_cancelled=true`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setLessons(Array.isArray(data) ? data : data.reservations || []);
      }
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };
  
  const fetchSalaryPayments = async () => {
    try {
      // Get trainer's debts and payments using the my-debts endpoint
      const res = await fetch(`${API}/trainers/${trainerId}/my-debts`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setTrainerDebts(data);
      }
    } catch (error) {
      console.error("Error fetching trainer debts:", error);
    }
  };
  
  const fetchBonusPayments = async () => {
    try {
      const res = await fetch(`${API}/trainers/${trainerId}/bonus-payments`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setBonusPayments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching bonus payments:", error);
    }
  };
  
  const fetchBusinessData = async () => {
    try {
      const [bizRes, branchesRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}`, { credentials: "include" }),
        fetch(`${API}/branches`, { credentials: "include" }),
      ]);
      
      if (bizRes.ok) {
        const biz = await bizRes.json();
        setBusinessAcceptedCards(biz.accepted_cards || []);
        setBusinessTrainingTypes(biz.training_types || ["one_on_one", "duet", "group"]);
        setBusinessWorkingHours(biz.working_hours || {});
      }
      
      if (branchesRes.ok) {
        const branches = await branchesRes.json();
        setBusinessBranches(branches.map(b => b.name || b));
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
    }
  };
  
  const handleSaveBonus = async () => {
    if (!bonusForm.amount) {
      toast.error(t("common:toast_lutfenTutarGirin"));
      return;
    }
    try {
      const response = await fetch(`${API}/trainers/${trainerId}/bonus-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(bonusForm.amount),
          payment_date: bonusForm.payment_date,
          reason: bonusForm.reason,
          payment_method: bonusForm.payment_method
        }),
      });
      if (response.ok) {
        toast.success(t('payments:additionalPaymentSaved'));
        setShowBonusModal(false);
        setBonusForm({ amount: "", payment_date: todayLocalString(), reason: "", payment_method: "cash" });
        fetchBonusPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:saveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const handleEndTrainer = async () => {
    if (!endReason) {
      toast.error(t("common:toast_lutfenAyrilmaNedeniSecin"));
      return;
    }
    
    // "Diğer" seçilmişse açıklama zorunlu
    if (endReason === "diger" && !endOtherReason.trim()) {
      toast.error(t("common:toast_lutfenAyrilmaNedeniniAciklayin"));
      return;
    }
    
    try {
      // Reason olarak "diger" seçilmişse açıklamayı da ekle
      const reasonToSend = endReason === "diger" 
        ? `${t("common:other")}: ${endOtherReason.trim()}` 
        : endReason;
      
      const response = await fetch(`${API}/trainers/${trainerId}/end`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          reason: reasonToSend,
          other_reason_detail: endReason === "diger" ? endOtherReason.trim() : null
        }),
      });
      if (response.ok) {
        toast.success(t("common:toast_antrenorAyrildiOlarakIsaretlendi"));
        setShowEndModal(false);
        setEndReason("");
        setEndOtherReason("");
        fetchTrainerData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }
  
  if (!trainer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-grey">{t("common:antrenorBulunamadi")}</p>
        <Button onClick={() => navigate("/dashboard/trainers")} className="rounded-xl">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t("common:goBack")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard/trainers")}
            className="rounded-xl flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 md:h-14 md:w-14 flex-shrink-0">
              <AvatarFallback className="bg-navy text-white text-lg md:text-xl">
                {trainer.name?.[0]?.toUpperCase() || "A"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold text-navy truncate">{trainer.name} {trainer.surname}</h1>
              <div className="flex items-center gap-2 text-sm text-grey mt-0.5">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                <span>{trainer.rating?.toFixed(1) || "5.0"} ({trainer.rating_count || 0})</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={(trainer.status === "active" || trainer.is_active) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
            {(trainer.status === "active" || trainer.is_active) ? t('admin:active') : t("common:left")}
          </Badge>
          {trainer.is_manager && (
            <Badge className="bg-purple-100 text-purple-700">
              <ShieldCheck className="w-3 h-3 mr-1" /> {t("admin:admin")}
            </Badge>
          )}
          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setShowEditModal(true)}>
            <Edit className="w-4 h-4 mr-1" /> {t("common:edit")}
          </Button>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowScheduleModal(true)}>
          <Clock className="w-5 h-5 text-blue-600" />
          <span className="text-sm">{t('settings:workingHours')}</span>
        </Button>
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowTimeBlockModal(true)}>
          <Lock className="w-5 h-5 text-orange-600" />
          <span className="text-sm">{t("common:timeLock")}</span>
        </Button>
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowCommissionReportModal(true)}>
          <TrendingUp className="w-5 h-5 text-green-600" />
          <span className="text-sm">{t("reports:commissionReportLabel")}</span>
        </Button>
        <Button variant="outline" className="rounded-xl flex flex-col items-center gap-1 h-auto py-3" onClick={() => setShowBonusModal(true)}>
          <Gift className="w-5 h-5 text-purple-600" />
          <span className="text-sm">{t("common:extraPayment")}</span>
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="w-max md:w-full inline-flex md:grid md:grid-cols-4 h-auto rounded-xl bg-slate-100 p-1 gap-1">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm py-2 px-3 whitespace-nowrap">{t('common:generalInfo')}</TabsTrigger>
            <TabsTrigger value="commissions" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm py-2 px-3 whitespace-nowrap">{t("reports:commissionDetails")}</TabsTrigger>
            <TabsTrigger value="lessons" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm py-2 px-3 whitespace-nowrap">{t("common:lessons")}</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm py-2 px-3 whitespace-nowrap">{t("employees:maasVeEkOdemeler")}</TabsTrigger>
          </TabsList>
        </div>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6 space-y-6">
              {/* Work Type & Salary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-grey mb-1">{t("common:workingType")}</p>
                  <div className="flex items-center gap-2">
                    {getWorkTypeIcon(trainer.work_type)}
                    <span className="text-lg font-semibold">{getWorkTypeLabel(trainer.work_type, t)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-grey mb-1">{t("common:startDate")}</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-navy" />
                    <span className="text-lg font-semibold">
                      {trainer.start_date ? format(new Date(trainer.start_date), "d MMMM yyyy", { locale: getDateLocale() }) : "-"}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Salary Info */}
              {(trainer.work_type === "salary_receiving" || trainer.work_type === "salary_and_commission_receiving") && trainer.current_salary > 0 && (
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-sm text-grey mb-1">{t('trainers:salary')}</p>
                  <span className="text-2xl font-bold text-green-700">{formatTurkishCurrency(trainer.current_salary)}</span>
                  <span className="text-grey ml-2">/ ay</span>
                </div>
              )}
              
              {/* Commission Giving Info */}
              {trainer.work_type === "commission_giving" && (
                <div className="p-4 bg-orange-50 rounded-xl">
                  <p className="text-sm text-grey mb-1">{t("reports:givenCommission")}</p>
                  <span className="text-2xl font-bold text-orange-700">
                    {trainer.giving_commission_type === "fixed" ? currencySymbol : "%"}{trainer.giving_commission_rate || 0}
                  </span>
                </div>
              )}
              
              {/* Rent Giving Info */}
              {trainer.work_type === "rent_giving" && (
                <div className="p-4 bg-amber-50 rounded-xl">
                  <p className="text-sm text-grey mb-1">{t("trainers:rentAmount")}</p>
                  <span className="text-2xl font-bold text-amber-700">{formatTurkishCurrency(trainer.rent_amount)}</span>
                  <span className="text-grey ml-2">/ ay</span>
                </div>
              )}
              
              {/* Branches */}
              <div>
                <p className="text-sm text-grey mb-2">{t('common:branches')}</p>
                <div className="flex flex-wrap gap-2">
                  {trainer.branches?.map((branch) => (
                    <Badge key={branch} variant="secondary" className="px-3 py-1">
                      <Dumbbell className="w-3 h-3 mr-1" /> {getLocalizedBranch(branch)}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="border-t pt-4">
                <p className="text-sm text-grey mb-3">{t('common:contactInfo')}</p>
                <div className="space-y-2">
                  {trainer.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-grey" />
                      <span>{trainer.email}</span>
                    </div>
                  )}
                  {trainer.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-grey" />
                      <span>{trainer.phone}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* End Trainer Button */}
              {(trainer.status === "active" || trainer.is_active) && (
                <div className="border-t pt-4">
                  <Button variant="outline" className="rounded-xl text-red-600 hover:bg-red-50" onClick={() => setShowEndModal(true)}>
                    <UserX className="w-4 h-4 mr-2" /> {t("common:markAsLeft")}
                  </Button>
                </div>
              )}
              
              {/* Visibility Card - Only for own profile AND only from trainer panel (not business dashboard) */}
              {isOwnProfile && !window.location.pathname.includes('/dashboard') && (
                <div className="border-t pt-4">
                  <TrainerVisibilityCard trainerId={trainerId} isOwnProfile={isOwnProfile} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-4 mt-4">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">{t("reports:commissionDetails")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Prim Tipi */}
              <div className="p-3 bg-blue-50 rounded-xl">
                <p className="text-xs text-grey mb-1">{t("reports:commissionType")}</p>
                <p className="text-sm font-medium text-navy flex items-center gap-2">
                  {trainer.commission_settings?.calculation_method === "per_package_sale" 
                    ? <><Banknote className="w-4 h-4 text-blue-600" /> {t("trainers:byPackageSale")}</> 
                    : <><TrendingUp className="w-4 h-4 text-green-600" /> {t("trainers:perLessonCommission")}</>}
                </p>
                {trainer.commission_settings?.calculation_method === "per_package_sale" && (
                  <p className="text-xs text-blue-600 mt-1">
                    {trainer.commission_settings?.commission_scope === "all_lessons"
                      ? t("packages:allPackageSalesCommShort")
                      : t("reports:ownLessonsCommShort")}
                  </p>
                )}
              </div>

              {/* Bu Ay Prim Özeti */}
              <div className="p-3 bg-purple-50 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-medium text-purple-700">{t("reports:thisMonthCommissionSummary")}</p>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                    {new Date().toLocaleString(i18n.language === 'en' ? 'en-US' : 'tr-TR', { month: 'long', year: 'numeric' })}
                  </Badge>
                </div>
                {trainerCommission ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-1.5 bg-white rounded-lg">
                      <p className="text-xs text-grey">{t("common:lessonLabel")}</p>
                      <p className="text-base font-bold text-navy">{trainerCommission.total_sessions || 0}</p>
                    </div>
                    <div className="text-center p-1.5 bg-white rounded-lg">
                      <p className="text-xs text-grey">{t('common:total')}</p>
                      <p className="text-base font-bold text-purple-700">{currencySymbol}{(trainerCommission.total_commission || 0).toLocaleString('tr-TR')}</p>
                    </div>
                    <div className="text-center p-1.5 bg-white rounded-lg">
                      <p className="text-xs text-grey">{t("common:pending")}</p>
                      <p className="text-base font-bold text-orange-600">{currencySymbol}{(trainerCommission.pending_amount || 0).toLocaleString('tr-TR')}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-grey text-center py-1">{t('common:loading')}</p>
                )}
              </div>

              {/* Prim Oranları */}
              {trainer.commission_settings && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-grey">{t("reports:commissionRates")}</p>
                  
                  {/* Kart Primleri */}
                  {businessAcceptedCards.length > 0 && trainer.commission_settings.card_commissions && (
                    <div>
                      <p className="text-xs font-medium text-grey mb-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        {t("reports:cardCommissions")}
                      </p>
                      <div className="space-y-2">
                        {businessAcceptedCards.map(cardName => {
                          const cardConfig = trainer.commission_settings?.card_commissions?.[cardName];
                          if (!cardConfig) return null;
                          return (
                            <div key={cardName} className="p-3 bg-amber-50 rounded-xl">
                              <p className="text-sm font-medium text-amber-700 mb-2">{cardName} ({cardConfig.type === "percentage" ? t("common:percentageBased") : t("common:fixed")})</p>
                              <div className="flex flex-wrap gap-2">
                                {businessTrainingTypes.map(type => (
                                  <span key={type} className="text-xs bg-amber-100 px-2 py-1 rounded-lg text-amber-800 font-medium">
                                    {getLocalizedTrainingType(type)}: {cardConfig.type === "percentage" ? "%" : currencySymbol}{cardConfig.rates?.[type] || 0}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Paket Primleri */}
                  {trainer.commission_settings.package_commissions && (
                    <div>
                      <p className="text-xs font-medium text-grey mb-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                        {t("packages:packageCommissions")} ({trainer.commission_settings.package_commissions.type === "percentage" ? t("common:percentageBased") : t("common:fixed")})
                      </p>
                      <div className="p-3 bg-emerald-50 rounded-xl">
                        <div className="flex flex-wrap gap-2">
                          {businessTrainingTypes.map(type => (
                            <span key={type} className="text-xs bg-emerald-100 px-2 py-1 rounded-lg text-emerald-800 font-medium">
                              {getLocalizedTrainingType(type)}: {trainer.commission_settings.package_commissions.type === "percentage" ? "%" : currencySymbol}{trainer.commission_settings.package_commissions.rates?.[type] || 0}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Prim ayarları yoksa */}
              {(!trainer.commission_settings || Object.keys(trainer.commission_settings).length === 0) && 
               (!trainer.commission_rates || Object.keys(trainer.commission_rates).length === 0) && (
                <p className="text-grey text-center py-4">{t("reports:commissionNotConfigured")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Lessons Tab */}
        <TabsContent value="lessons" className="space-y-4 mt-4">
          {/* Bu Ay Performans Özeti */}
          <Card className="rounded-2xl border-0 shadow-sm bg-gradient-to-r from-navy to-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-medium">{t("common:thisMonthPerformance")}</p>
                <Badge className="bg-white/20 text-white text-xs">
                  {new Date().toLocaleString(i18n.language === 'en' ? 'en-US' : 'tr-TR', { month: 'long', year: 'numeric' })}
                </Badge>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-white">{monthlyPerformance.totalLessons}</p>
                  <p className="text-xs text-white/70">{t('trainers:totalLessons')}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-green-300">{monthlyPerformance.completedLessons}</p>
                  <p className="text-xs text-white/70">{t('common:completed')}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-300">{currencySymbol}{monthlyPerformance.totalCommission.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-white/70">{t("reports:totalCommission")}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-cyan-300">%{monthlyPerformance.completionRate}</p>
                  <p className="text-xs text-white/70">{t("common:completionRate")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">{t("common:lessons")} ({filteredLessons.length} / {lessons.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtreler */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
                  <Input
                    placeholder={t("common:ph_uyeAdiAra")}
                    value={lessonSearchTerm}
                    onChange={(e) => setLessonSearchTerm(e.target.value)}
                    className="pl-9 rounded-lg"
                  />
                </div>
                <Select value={lessonBranchFilter} onValueChange={setLessonBranchFilter}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder={t('common:branch')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common:tumBranslar")}</SelectItem>
                    {lessonBranches.map(branch => (
                      <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={lessonStatusFilter} onValueChange={setLessonStatusFilter}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder={t('common:status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common:allStatuses")}</SelectItem>
                    <SelectItem value="pending">{t('common:pending')}</SelectItem>
                    <SelectItem value="cancelled">{t("trainers:cancelledOrLate")}</SelectItem>
                    <SelectItem value="confirmed">{t('admin:approved')}</SelectItem>
                    <SelectItem value="completed">{t('common:completed')}</SelectItem>
                    <SelectItem value="burned">{t("common:yakildi")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredLessons.length > 0 ? (
                <div className="space-y-3">
                  {paginatedLessons.map((lesson) => (
                    <div key={lesson.reservation_id || lesson._id} className={`p-4 rounded-xl ${lesson.status === "cancelled" ? "bg-red-50 opacity-75" : "bg-slate-50"}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className={`text-white text-sm ${lesson.status === "cancelled" ? "bg-red-400" : "bg-navy"}`}>
                              {lesson.member_name?.[0] || "M"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className={`font-medium ${lesson.status === "cancelled" ? "line-through text-grey" : ""}`}>{lesson.member_name || t("common:member")}</p>
                            <p className="text-sm text-grey">
                              {lesson.date ? format(new Date(lesson.date), "d MMM yyyy", { locale: getDateLocale() }) : "-"} • {lesson.start_time} - {lesson.end_time}
                            </p>
                            {lesson.package_name && (
                              <p className="text-xs text-blue-600 mt-1">
                                📦 {lesson.package_name} ({lesson.training_type === "one_on_one" ? t("common:oneOnOne") : lesson.training_type === "duet" ? t("common:duet") : "Grup"})
                              </p>
                            )}
                            {lesson.branch && (
                              <Badge variant="secondary" className="mt-1 text-xs">{getLocalizedBranch(lesson.branch)}</Badge>
                            )}
                            {lesson.status === "cancelled" && lesson.cancellation_reason && (
                              <p className="text-xs text-red-600 mt-1">{t("trainers:cancelReason")}: {lesson.cancellation_reason}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {getReservationStatusBadge(lesson.status, t)}
                          {lesson.status !== "cancelled" && (lesson.frozen_commission || lesson.commission_amount) > 0 && (
                            <p className="text-sm text-green-600 mt-1">{formatTurkishCurrency(lesson.frozen_commission || lesson.commission_amount)}</p>
                          )}
                        </div>
                      </div>
                      {lesson.lesson_note && (
                        <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex gap-2" data-testid={`trainer-lesson-note-${lesson.reservation_id}`}>
                          <span className="font-medium">📝 {t("reservations:lessonNote", "Ders Açıklaması")}:</span>
                          <span className="flex-1 whitespace-pre-wrap break-words">{lesson.lesson_note}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t mt-4">
                    <div className="flex items-center gap-3 text-sm text-grey">
                      <span>{t("common:totalRecords", { count: filteredLessons.length })}</span>
                      <div className="flex items-center gap-2">
                        <span>{t("common:show")}</span>
                        <Select value={lessonPageSize.toString()} onValueChange={(v) => setLessonPageSize(Number(v))}>
                          <SelectTrigger className="w-20 h-8 rounded-lg" data-testid="trainer-lessons-page-size">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-lg"
                        disabled={lessonPage <= 1}
                        onClick={() => setLessonPage(p => Math.max(1, p - 1))}
                        data-testid="trainer-lessons-prev"
                      >‹</Button>
                      <span className="text-sm px-2">{lessonPage} / {totalLessonPages}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-lg"
                        disabled={lessonPage >= totalLessonPages}
                        onClick={() => setLessonPage(p => Math.min(totalLessonPages, p + 1))}
                        data-testid="trainer-lessons-next"
                      >›</Button>
                    </div>
                  </div>
                </div>
              ) : lessons.length > 0 ? (
                <p className="text-grey text-center py-8">{t("common:filtrelereUygunDersBulunamadi")}</p>
              ) : (
                <p className="text-grey text-center py-8">{t("common:henuzDersKaydiYok")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4 mt-4">
          {/* Özet Kartlar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t("employees:expectedSalary")}</p>
              <p className="text-lg font-bold text-blue-700">
                {formatTurkishCurrency(trainerDebts?.salary?.total_pending || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t("employees:receivedSalary")}</p>
              <p className="text-lg font-bold text-green-700">
                {formatTurkishCurrency(trainerDebts?.salary?.total_paid || 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t("common:extraPayments")}</p>
              <p className="text-lg font-bold text-purple-700">
                {formatTurkishCurrency(bonusPayments.reduce((sum, b) => sum + (b.amount || 0), 0))}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl text-center">
              <p className="text-xs text-grey">{t('payments:totalReceivable')}</p>
              <p className="text-lg font-bold text-amber-700">
                {formatTurkishCurrency((trainerDebts?.salary?.total_pending || 0))}
              </p>
            </div>
          </div>

          {/* Maaş Hak Edişleri */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                {t("employees:salaryPaymentsLabel")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trainerDebts?.salary?.debts?.length > 0 || trainerDebts?.salary?.payments?.length > 0 ? (
                <div className="space-y-3">
                  {/* Bekleyen Maaşlar */}
                  {trainerDebts?.salary?.debts?.filter(d => d.status !== "paid").map((debt, index) => (
                    <div key={debt.debt_id || index} className="p-4 bg-amber-50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-medium text-amber-800">{debt.year} - {String(debt.month).padStart(2, '0')}</p>
                        <p className="text-sm text-amber-600">
                          {t("common:amount")}: {formatTurkishCurrency(debt.amount || 0)}
                          {debt.remaining_amount !== debt.amount && ` (Kalan: ${formatTurkishCurrency(debt.remaining_amount || 0)})`}
                        </p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">
                        {debt.status === "partial" ? t("common:partiallyPaid") : t("common:pending")}
                      </Badge>
                    </div>
                  ))}
                  
                  {/* Ödenen Maaşlar */}
                  {trainerDebts?.salary?.payments?.map((payment, index) => (
                    <div key={payment.payment_id || index} className="p-4 bg-green-50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-medium text-green-800">
                          {payment.period || (payment.payment_date ? format(new Date(payment.payment_date), "MMMM yyyy", { locale: getDateLocale() }) : "-")}
                        </p>
                        <p className="text-sm text-green-600">
                          {payment.payment_date ? format(new Date(payment.payment_date), "d MMM yyyy", { locale: getDateLocale() }) : "-"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-700">{formatTurkishCurrency(payment.amount)}</p>
                        <Badge className="bg-green-100 text-green-700">{t('payments:paid')}</Badge>
                      </div>
                    </div>
                  ))}
                  
                  {/* Bekleyen yoksa ve ödenen de yoksa */}
                  {!trainerDebts?.salary?.debts?.length && !trainerDebts?.salary?.payments?.length && (
                    <p className="text-grey text-center py-4">{t("employees:noSalaryRecords")}</p>
                  )}
                </div>
              ) : (
                <p className="text-grey text-center py-8">{t("employees:noSalaryPaymentRecords")}</p>
              )}
            </CardContent>
          </Card>
          
          {/* Ek Ödemeler */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                {t("common:extraPaymentsLabel")}
              </CardTitle>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl" onClick={() => setShowBonusModal(true)}>
                <Plus className="w-4 h-4 mr-1" /> {t("common:makeExtraPayment2")}
              </Button>
            </CardHeader>
            <CardContent>
              {bonusPayments.length > 0 ? (
                <div className="space-y-3">
                  {bonusPayments.map((bonus, index) => (
                    <div key={bonus.payment_id || index} className="p-4 bg-purple-50 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="font-medium text-purple-800">{bonus.reason || t("common:extraPayment")}</p>
                        <p className="text-sm text-purple-600">
                          {bonus.payment_date ? format(new Date(bonus.payment_date), "d MMM yyyy", { locale: getDateLocale() }) : "-"}
                          {bonus.payment_method && ` • ${bonus.payment_method === "cash" ? t('payments:cash') : bonus.payment_method === "bank" ? "Banka" : bonus.payment_method}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-purple-700">{formatTurkishCurrency(bonus.amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-grey text-center py-8">{t("common:noExtraPaymentRecords")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Trainer Schedule Modal */}
      <TrainerScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        trainer={trainer}
        businessId={businessId}
        businessWorkingHours={businessWorkingHours}
      />
      
      {/* Trainer Time Block Modal */}
      <TrainerTimeBlockModal
        isOpen={showTimeBlockModal}
        onClose={() => setShowTimeBlockModal(false)}
        trainer={trainer}
        businessId={businessId}
        canEdit={true}
      />
      
      {/* Trainer Commission Report Modal */}
      <TrainerCommissionReportModal
        isOpen={showCommissionReportModal}
        onClose={() => setShowCommissionReportModal(false)}
        trainer={trainer}
        businessId={businessId}
      />
      
      {/* Trainer Edit Modal */}
      <TrainerEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        trainer={trainer}
        businessId={businessId}
        businessBranches={businessBranches}
        businessAcceptedCards={businessAcceptedCards}
        businessTrainingTypes={businessTrainingTypes}
        onSuccess={() => fetchTrainerData()}
      />
      
      {/* Bonus Payment Modal */}
      <Dialog open={showBonusModal} onOpenChange={setShowBonusModal}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("common:makeExtraPayment")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-grey mb-4">{t("common:createExtraPaymentFor", { name: `${trainer.name} ${trainer.surname}` })}</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="bonus-amount">{t("common:amount")} ({currencySymbol}) *</Label>
              <Input
                id="bonus-amount"
                type="number"
                placeholder="0"
                value={bonusForm.amount}
                onChange={(e) => setBonusForm({...bonusForm, amount: e.target.value})}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label htmlFor="bonus-date">{t("common:paymentDate")}</Label>
              <DateInputTR
        id="bonus-date" value={bonusForm.payment_date}
        onChange={(e) => setBonusForm({...bonusForm, payment_date: e.target.value})}
        className="rounded-xl mt-1" />
            </div>
            <div>
              <Label htmlFor="bonus-reason">{t('common:description')}</Label>
              <Input
                id="bonus-reason"
                placeholder={t("admin:ph_ekOdemeNedeni")}
                value={bonusForm.reason}
                onChange={(e) => setBonusForm({...bonusForm, reason: e.target.value})}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t('payments:paymentMethod')}</Label>
              <Select value={bonusForm.payment_method} onValueChange={(v) => setBonusForm({...bonusForm, payment_method: v})}>
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowBonusModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={handleSaveBonus} className="rounded-xl bg-navy">{t('common:save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* End Trainer Modal */}
      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("common:trainerDeparture")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-grey">
              <strong>{trainer.name} {trainer.surname}</strong> {t("common:specifyLeaveReason")}
            </p>
            <div>
              <Label>{t("common:leavingReason")}</Label>
              <Select value={endReason} onValueChange={(val) => {
                setEndReason(val);
                if (val !== "diger") setEndOtherReason("");
              }}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_nedenSecin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="istifa">{t("common:resignation")}</SelectItem>
                  <SelectItem value="performans">{t("common:poorPerformance")}</SelectItem>
                  <SelectItem value="disiplin">Disiplin</SelectItem>
                  <SelectItem value="karsilikli">{t("common:mutualAgreement")}</SelectItem>
                  <SelectItem value="diger">{t('common:other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Diğer seçildiğinde açıklama alanı */}
            {endReason === "diger" && (
              <div>
                <Label>{t("admin:departureReasonExplanation")}</Label>
                <Input
                  value={endOtherReason}
                  onChange={(e) => setEndOtherReason(e.target.value)}
                  placeholder={t("common:ph_ayrilmaNedeniniAciklayin")}
                  className="rounded-xl mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEndModal(false)} className="rounded-xl">{t('common:cancel')}</Button>
            <Button onClick={handleEndTrainer} className="rounded-xl bg-red-600 hover:bg-red-700 text-white">{t("common:markAsLeft")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
